import { c as _c } from "react/compiler-runtime";
import type { CouponIndex } from '@svs/nova-poolbetting/schema/coupon';
import type { Draw, DrawNumber } from '@svs/nova-poolbetting/schema/draw';
import type { Definition } from '@svs/nova-poolbetting/schema/frameworks';
import type { BettingEvent, EventNumber, EventType, SportType } from '@svs/nova-poolbetting/schema/sport';
import { Card } from '@svs/ui/react/card';
import { type ReactNode, Suspense } from 'react';
import { TipsinfoValuesContainer } from '@/components/index.browser';
import CouponOutcomesContainer from './CouponOutcomesContainer';
type CouponRowEvent = Draw['events'][number];
export type CouponRowOutcomeItemProps = {
  couponIndex: CouponIndex;
  selectedCouponIndex: CouponIndex;
  hasMultipleCoupons: boolean | undefined;
  event: CouponRowEvent;
  eventType: EventType;
  sportType: SportType;
  drawNumber: DrawNumber;
  product: Definition['moduleEngine'];
  eventTypeStatisticsId: BettingEvent['eventTypeStatisticsId'] | undefined;
  handleClick: ({
    eventNumber
  }: {
    eventNumber: EventNumber;
  }) => void;
};

/**
 * Renders one outcome row item for a coupon event.
 *
 * @param root0 Component props.
 * @param root0.couponIndex Coupon index for the rendered row.
 * @param root0.selectedCouponIndex Currently selected coupon index.
 * @param root0.hasMultipleCoupons Indicates whether multiple coupons are shown.
 * @param root0.event Event data for the current row item.
 * @param root0.eventType Event type used to render outcomes.
 * @param root0.sportType Sport type for the event.
 * @param root0.drawNumber Draw number for the current coupon.
 * @param root0.product Product identifier used by tips info.
 * @param root0.eventTypeStatisticsId Statistics id for tips info.
 * @param root0.handleClick Click handler for opening statistics.
 * @returns Outcome row item component.
 */
export function CouponRowOutcomeItem(t0) {
  const $ = _c(25);
  const {
    couponIndex,
    selectedCouponIndex,
    hasMultipleCoupons,
    event,
    eventType,
    sportType,
    drawNumber,
    product,
    eventTypeStatisticsId,
    handleClick
  } = t0;
  const isSelectedCoupon = couponIndex === selectedCouponIndex;
  const t1 = `[clip-path:inset(-5px_-5px_-5px_0px)] lg:[clip-path:none] ${hasMultipleCoupons && isSelectedCoupon ? "bg-surface-elevated lg:bg-secondary-variant" : "bg-surface-elevated"} ${hasMultipleCoupons ? "lg:border-stroke-low h-full lg:border-l" : "h-full"} relative m-0 flex w-full flex-col items-center p-3 pl-0 lg:p-4 lg:pl-4`;
  const t2 = `Statistik för ${event.eventDescription}`;
  const t3 = `absolute inset-0 cursor-pointer ${hasMultipleCoupons ? "lg:hidden" : ""}`;
  let t4;
  if ($[0] !== event.eventNumber || $[1] !== handleClick) {
    t4 = () => {
      handleClick({
        eventNumber: event.eventNumber
      });
    };
    $[0] = event.eventNumber;
    $[1] = handleClick;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t2 || $[4] !== t3 || $[5] !== t4) {
    t5 = <div role="link" tabIndex={0} aria-label={t2} className={t3} onClick={t4} />;
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const t6 = `${event.eventNumber}-${couponIndex}`;
  let t7;
  if ($[7] !== couponIndex || $[8] !== drawNumber || $[9] !== event.eventNumber || $[10] !== eventType || $[11] !== sportType || $[12] !== t6) {
    t7 = <CouponOutcomesContainer className="min-h-10 w-full items-center pr-1 pl-1" key={t6} eventNumber={event.eventNumber} sportType={sportType} eventType={eventType} couponIndex={couponIndex} drawNumber={drawNumber} />;
    $[7] = couponIndex;
    $[8] = drawNumber;
    $[9] = event.eventNumber;
    $[10] = eventType;
    $[11] = sportType;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== drawNumber || $[15] !== event.eventNumber || $[16] !== eventTypeStatisticsId || $[17] !== isSelectedCoupon || $[18] !== product) {
    t8 = isSelectedCoupon && <Suspense><TipsinfoValuesContainer settingsKey="PLAY" eventTypeStatisticsId={eventTypeStatisticsId} drawNumber={drawNumber} eventNumber={event.eventNumber} product={product} /></Suspense>;
    $[14] = drawNumber;
    $[15] = event.eventNumber;
    $[16] = eventTypeStatisticsId;
    $[17] = isSelectedCoupon;
    $[18] = product;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t1 || $[21] !== t5 || $[22] !== t7 || $[23] !== t8) {
    t9 = <Card className={t1} data-style-element={true}>{t5}{t7}{t8}</Card>;
    $[20] = t1;
    $[21] = t5;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}